import { Typography } from 'app/components';
import { IconButton } from 'app/components/ui/IconButton/IconButton';
import { i18n } from 'app/utils/i18n';
import { useContext } from 'react';
import { AddUser } from '../AddUser/AddUser';
import Tippy from '@tippyjs/react';
import { RemoveUser } from '../RemoveUser/RemoveUser';
import { useParams } from 'react-router-dom';
import { useGetPermissions, useAddPermissionToUser, useRemovePermissionFromUser } from 'app/api/permission-queries';
import { useUsersInOrganization } from 'app/api/auth/user-queries';
import { UserContext } from 'app/state/contexts/UserContext';
import { dispatchMessage, EventType } from 'app/useToasts';
import './TagPermissions.scss';

interface UserContextType {
  selectedOrganization?: number;
}

interface User {
  id: number;
  firstName: string;
  lastName: string;
  userName: string;
}

const TagPermissions = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const userContext = useContext(UserContext) as unknown as UserContextType;
  const { data: permissions } = useGetPermissions(documentId);
  const { data: users = [] } = useUsersInOrganization(userContext?.selectedOrganization) as { data: User[] };
  const { mutate: addPermission } = useAddPermissionToUser();
  const { mutate: removePermission } = useRemovePermissionFromUser();

  const availableRoles = [
    {
      permission: 'TAG_ADMIN',
      name: i18n('tag.admin.label'),
    },
    {
      permission: 'TAG_EDITOR',
      name: i18n('tag.editor.label'),
    },
    {
      permission: 'TAG_VIEWER',
      name: i18n('tag.viewer.label'),
    },
  ];

  const addUserPermission = (userName: string, permission: string) => {
    if (!documentId) return;

    const user = users.find((u) => u.userName === userName);
    if (!user) {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'error',
        text: i18n('document-permissions.add-user.not-found'),
        removeTime: 4000,
      });
      return;
    }

    addPermission({
      documentId,
      permission,
      userId: user.id,
    });
  };

  const removeUserPermission = (userId: number, permission: string) => {
    if (!documentId) return;

    removePermission({
      documentId,
      permission,
      userId,
    });
  };

  return (
    <div className="tag-access">
      {availableRoles.map((role) => (
        <div key={role.name}>
          <div className="tag-access__header">
            <Typography variant="body-8" fontWeight="semibold">
              {role.name}
            </Typography>

            <Tippy
              trigger="click"
              placement="bottom"
              offset={[20, 10]}
              interactive
              content={<AddUser onClick={(userName) => addUserPermission(userName, role.permission)} />}
            >
              <span>
                <IconButton icon="add" />
              </span>
            </Tippy>
          </div>

          <div>
            <table className="tag-access__permissions">
              <tbody>
                {permissions && permissions.userDocumentPermissions.filter((permissions) => permissions.permission === role.permission).length > 0 ? (
                  permissions.userDocumentPermissions
                    .filter((permissions) => permissions.permission === role.permission)
                    .map((member) => {
                      const user = users.find((user) => user.id === member.userId);

                      if (!user) return null;

                      const initials = `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`;
                      const fullName = `${user.firstName} ${user.lastName}`;

                      return (
                        <tr key={user.id}>
                          <td>
                            <span className="initials">{initials}</span>
                            {fullName}
                            <Tippy
                              placement="bottom"
                              trigger="click"
                              offset={[20, 10]}
                              interactive
                              content={<RemoveUser onClick={() => removeUserPermission(user.id, role.permission)} />}
                            >
                              <span>
                                <IconButton icon="menu" className="icon-btn" variant="transparent" />
                              </span>
                            </Tippy>
                          </td>
                        </tr>
                      );
                    })
                ) : (
                  <tr>
                    <td>{i18n('tag.access.no.user')} </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default TagPermissions;
